import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Brain, RefreshCw, Loader2, Lock, TrendingDown, Sparkles } from "lucide-react";
import { useVehicle } from "../../../lib/vehicleContext";
import { warrantyFor } from "../../../lib/brand";
import {
  useSubscription,
  openSubscribeGate,
} from "../../../lib/subscription";
import { getClimate } from "../../../lib/climateData";
import { detectAnomalies } from "../../../lib/intelligenceScore";
import { coachInsights } from "../../../lib/insightSummaries";
import { API_BASE, VEHICLE_USAGE_HINTS, Recommendation } from "./_shared";
import { BatteryCoachCard } from "../BatteryCoachCard";
import { CollapseSection } from "../CollapseSection";

// localStorage cache for AI narrative — persists across cold starts so the
// user isn't hit with a 15–20s Claude call every time they reopen /app.
// Cached ONLY when we have real Tesla telemetry — keyed by VIN (which the
// Fleet API returns) so switching vehicles or falling back to a mock
// catalog never leaks stale output into a different pack's dashboard.
const AI_CACHE_KEY = "eviq.ai_narrative_cache_v2";
const AI_CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours

function readAiCache(vin, stateCode) {
  if (!vin) return null;
  try {
    const raw = window.localStorage.getItem(AI_CACHE_KEY);
    if (!raw) return null;
    const bag = JSON.parse(raw);
    const entry = bag?.[`${vin}::${stateCode}`];
    if (!entry?.data || !entry?.savedAt) return null;
    if (Date.now() - entry.savedAt > AI_CACHE_MAX_AGE_MS) return null;
    return entry;
  } catch (_e) {
    return null;
  }
}

function writeAiCache(vin, stateCode, data) {
  if (!vin) return; // never cache non-live output
  try {
    const raw = window.localStorage.getItem(AI_CACHE_KEY);
    const bag = raw ? JSON.parse(raw) : {};
    bag[`${vin}::${stateCode}`] = { data, savedAt: Date.now() };
    window.localStorage.setItem(AI_CACHE_KEY, JSON.stringify(bag));
  } catch (_e) {
    // best-effort cache — never throw
  }
}

/**
 * Return a compact human-readable label for a live Tesla vehicle:
 *   "2021 Model 3 · VIN…F4K5"
 * Falls back to just the vehicle name if VIN isn't present yet.
 */
function liveVehicleLabel(vehicle) {
  if (!vehicle) return "";
  const base = `${vehicle.year || ""} ${vehicle.short || vehicle.name || ""}`.trim();
  const vin = vehicle.vin || vehicle.location?.vin;
  if (vin && typeof vin === "string" && vin.length >= 4) {
    return `${base} · VIN…${vin.slice(-4)}`;
  }
  return base;
}

export const AIView = ({ stateCode }) => {
  const { t } = useTranslation();
  const { vehicle } = useVehicle();
  const { isSubscribed } = useSubscription();
  const climate = useMemo(() => getClimate(stateCode), [stateCode]);
  const hints = VEHICLE_USAGE_HINTS[vehicle.id] || VEHICLE_USAGE_HINTS["model-3-lr"];
  const [ai, setAi] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [_savedAt, setSavedAt] = useState(null);

  // AI narrative is only valid when the vehicle payload came from a real
  // Tesla OAuth session (vehicle.live === true). Otherwise the fallback
  // catalog would leak "Model 3 LR" cached output into every free-preview
  // visitor's dashboard, which is exactly what the user complained about.
  const isLive = Boolean(vehicle?.live);
  const cacheVin = vehicle?.vin || vehicle?.location?.vin || null;

  const usage = useMemo(() => ({
    avg_daily_miles: 34,
    supercharger_pct: hints.supercharger_pct,
    typical_daily_soc_max: 90,
    parked_at_100pct_days_per_month: hints.parked_at_100pct_days_per_month,
    preconditions_in_winter: hints.preconditions_in_winter,
  }), [hints]);

  const anomalies = useMemo(
    () =>
      detectAnomalies({
        monthlyLoss: Math.abs(vehicle.delta30d || 0.2),
        fleetAvgLoss: 0.18,
        superchargerPct: hints.supercharger_pct,
        parkedAt100PctDaysPerMonth: hints.parked_at_100pct_days_per_month,
        climateZone: climate.zone,
        phantomDrainPct: 1.2,
        cellImbalanceMv: 14,
      }),
    [vehicle, hints, climate]
  );

  const fetchAi = async () => {
    // Refuse to call the LLM unless we're operating on real Tesla telemetry.
    // This keeps the AI narrative from being generated against the fallback
    // Model 3 LR catalog when no vehicle is signed in.
    if (!isLive) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/api/intelligence/analyze`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model_label: `${vehicle.year} ${vehicle.name}`,
          model_year: vehicle.year,
          epa_range_new: vehicle.epaRange,
          current_soh: vehicle.sohCurrent,
          delta_30d: vehicle.delta30d || -0.2,
          odometer: vehicle.odometer || 50000,
          warranty_years: warrantyFor(vehicle).years,
          warranty_miles: warrantyFor(vehicle).miles,
          warranty_threshold: warrantyFor(vehicle).threshold,
          climate,
          usage,
          anomalies: anomalies.map((a) => a.label),
          insights: coachInsights(vehicle, climate, Math.abs(vehicle.delta30d || 0.2)),
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setAi(data);
      setSavedAt(Date.now());
      writeAiCache(cacheVin, stateCode, data);
    } catch (_e) {
      setError("AI analysis temporarily unavailable — try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only operate against live Tesla data. Without a real session the
    // LiveGate parent already renders the "Sign in with Tesla" prompt, so
    // this branch is defensive.
    if (!isLive) {
      setAi(null);
      setSavedAt(null);
      return;
    }
    // Hydrate from localStorage first — instant paint across cold starts.
    // Cache is keyed by VIN so a different pack never sees another pack's
    // narrative. Fresh (<12h) entries skip the network call; older entries
    // render immediately while a background refetch runs.
    const cached = readAiCache(cacheVin, stateCode);
    if (cached) {
      setAi(cached.data);
      setSavedAt(cached.savedAt);
      const age = Date.now() - cached.savedAt;
      if (age < AI_CACHE_MAX_AGE_MS / 2) {
        return;
      }
    }
    fetchAi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLive, cacheVin, stateCode]);

  return (
    <div className="pt-4 space-y-4" data-testid="mobile-app-view-ai">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">
          {t("app.ai_eyebrow", "EVIQ Intelligence™")}
        </div>
        <div className="font-display text-lg text-white font-semibold leading-tight">
          {t("app.ai_headline_1", "Predictive insights,")}{" "}
          <span className="brand-text-gradient">
            {t("app.ai_headline_2", "not just numbers.")}
          </span>
        </div>
      </div>

      <CollapseSection id="ai-coach" eyebrow="Interactive" title="Ask the Battery Coach" defaultOpen>
        <BatteryCoachCard vehicle={vehicle} climate={climate} stateCode={stateCode} />
      </CollapseSection>

      <div className="flex items-center gap-2">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center border border-white/10"
          style={{ background: "rgba(0,229,255,0.08)" }}
        >
          <Brain className="w-4 h-4" style={{ color: "#00E5FF" }} strokeWidth={2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm text-white font-semibold">AI narrative</div>
          <div className="font-mono text-[10px] text-zinc-500 flex items-center gap-1" data-testid="mobile-app-ai-status">
            {loading ? (
              <>
                <Loader2 className="w-2.5 h-2.5 animate-spin" strokeWidth={2.5} />
                Analyzing your {vehicle?.brand === "ford" ? "Ford" : vehicle?.brand === "volvo" ? "Volvo" : "Tesla"} telemetry…
              </>
            ) : (
              <>
                <span className="w-1 h-1 rounded-full bg-[#39FF14] animate-pulse" />
                Live · {liveVehicleLabel(vehicle)}
              </>
            )}
          </div>
        </div>
        <button
          onClick={() => {
            if (!isSubscribed) {
              openSubscribeGate("ai-refresh");
              return;
            }
            fetchAi();
          }}
          data-testid="mobile-app-ai-refresh"
          className="w-8 h-8 rounded-full flex items-center justify-center border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] transition-colors relative"
          aria-label={isSubscribed ? "Refresh" : "Subscribe to refresh AI narrative"}
          title={isSubscribed ? "Refresh AI narrative" : "Subscribe to refresh — one preview narrative per visit"}
        >
          {isSubscribed ? (
            <RefreshCw
              className={`w-3.5 h-3.5 text-zinc-400 ${loading ? "animate-spin" : ""}`}
              strokeWidth={2}
            />
          ) : (
            <Lock className="w-3.5 h-3.5" style={{ color: "#FFB020" }} strokeWidth={2.2} />
          )}
        </button>
      </div>

      {error && (
        <div className="rounded-lg border border-[#FFB020]/30 bg-[#FFB020]/[0.05] p-3 text-[11px] text-[#FFB020]">
          {error}
        </div>
      )}

      {loading && !ai && (
        <div className="space-y-3" data-testid="mobile-app-ai-skeleton">
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 space-y-2">
            <div className="h-4 w-3/4 rounded bg-white/[0.06] animate-pulse" />
            <div className="h-3 w-full rounded bg-white/[0.04] animate-pulse" />
            <div className="h-3 w-full rounded bg-white/[0.04] animate-pulse" />
            <div className="h-3 w-5/6 rounded bg-white/[0.04] animate-pulse" />
          </div>
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="rounded-xl border border-white/10 bg-white/[0.02] p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <div className="h-4 w-10 rounded bg-white/[0.06] animate-pulse" />
                  <div className="h-4 flex-1 rounded bg-white/[0.06] animate-pulse" />
                </div>
                <div className="h-3 w-4/5 rounded bg-white/[0.04] animate-pulse" />
              </div>
            ))}
          </div>
        </div>
      )}

      {ai?.narrative && (
        <div className="rounded-xl border border-white/10 bg-gradient-to-b from-[#00E5FF]/[0.04] to-transparent p-4">
          <div className="font-display text-base text-white font-semibold leading-snug mb-2">
            {ai.narrative.headline}
          </div>
          <p className="text-xs text-zinc-300 leading-relaxed">{ai.narrative.body}</p>
        </div>
      )}

      {ai?.recommendations?.length > 0 && (
        <div>
          <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-2 px-1">
            Recommendations
          </div>
          <div className="space-y-2.5" data-testid="mobile-app-ai-recs">
            {ai.recommendations.map((r, i) => (
              <Recommendation key={i} {...r} />
            ))}
          </div>
        </div>
      )}

      {ai?.anomaly_notes && (
        <div className="rounded-xl border border-[#FFB020]/25 bg-[#FFB020]/[0.04] p-3">
          <div className="flex items-center gap-1.5 mb-1">
            <TrendingDown className="w-3 h-3" style={{ color: "#FFB020" }} strokeWidth={2} />
            <span className="font-mono text-[9px] uppercase text-[#FFB020]">Anomaly notes</span>
          </div>
          <p className="text-[11px] text-zinc-300 leading-relaxed">{ai.anomaly_notes}</p>
        </div>
      )}

      <div className="pt-2 border-t border-white/5 flex items-center gap-2">
        <Sparkles className="w-3 h-3" style={{ color: "#00E5FF" }} strokeWidth={2} />
        <span className="font-mono text-[10px] text-zinc-500">
          Powered by EVIQ AI Engine™ · Claude Sonnet 4.5
        </span>
      </div>
    </div>
  );
};
